// src/components/Footer.js
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { usePrivacy } from "../context/PrivacyContext";
import { Moon, Sun, Home, Eye, EyeOff } from "lucide-react";

// Discord Icon SVG
const DiscordIcon = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

const DISCORD_INVITE_URL = "https://discord.gg/F3m5t9kyyF";

const Footer = () => {
  const year = new Date().getFullYear();
  const { theme, toggleTheme } = useTheme();
  const { privacyMode, togglePrivacyMode } = usePrivacy();
  const navigate = useNavigate();

  return (
    <footer
      role="contentinfo"
      className="fixed bottom-0 right-0 left-0 z-10 border-t"
      style={{
        // Footer spans the full viewport width (left: 0) so it visually
        // connects with the bottom of the sidebar — instead of starting
        // at the sidebar's right edge with a visible seam in the corner.
        // The navbar (z-[1100]) sits on top of the footer's leftmost
        // portion, so the user sees an unbroken L-shape regardless of
        // whether the sidebar is collapsed (60px) or expanded (240px).
        //
        // Heavy transparency (10% card opacity) + light blur lets the
        // ambient body gradient read through almost completely. Tuned via
        // /gradient-playground.
        backgroundColor: 'color-mix(in srgb, var(--color-card) 10%, transparent)',
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        borderColor: 'var(--color-border)'
      }}
    >
      <div
        // Push the footer's actual content past the sidebar so the icons
        // never sit under the navbar even though the bar itself extends
        // full-width. The transition matches the sidebar's collapse anim.
        className="h-10 px-4 flex items-center justify-between motion-safe:transition-[padding-left] motion-safe:duration-200 motion-safe:ease-out"
        style={{ paddingLeft: 'calc(var(--nav-w, 0px) + 1rem)' }}
      >
        {/* Left: Welcome page and Discord links */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate('/welcome', { state: { fromApp: true } })}
            type="button"
            data-tour="footer-home"
            style={{
              backgroundColor: 'var(--color-card)',
              borderColor: 'var(--color-border)'
            }}
            className="inline-flex h-8 w-8 items-center justify-center
                       rounded-lg border
                       hover:opacity-80
                       transition-colors duration-150"
            title="View Welcome Page"
            aria-label="View Welcome Page"
          >
            <Home className="w-4 h-4 text-theme-muted" />
          </button>
          <a
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-tour="footer-discord"
            style={{
              backgroundColor: 'var(--color-card)',
              borderColor: 'var(--color-border)'
            }}
            className="inline-flex h-8 w-8 items-center justify-center
                       rounded-lg border
                       hover:opacity-80
                       transition-colors duration-150"
            title="Join our Discord"
            aria-label="Join our Discord"
          >
            <DiscordIcon className="w-4 h-4 text-theme-muted" />
          </a>
        </div>

        {/* Center: Copyright */}
        <p className="text-xs text-theme-muted flex-1 text-center min-w-0 truncate px-2">
          © {year} Nexus Inventory Manager · NOTEAZY L.L.C.
        </p>

        {/* Right: Blur (privacy) toggle + Theme toggle */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {/* Blur mode quick-toggle — pulls from the same global state as
              Settings → Privacy → Blur mode and the Dashboard eye-icon
              button. When on, the button tints with the accent color so
              users have a clear "this is active" affordance. */}
          <button
            onClick={togglePrivacyMode}
            type="button"
            data-tour="privacy-toggle"
            style={{
              backgroundColor: privacyMode
                ? 'color-mix(in srgb, rgb(var(--accent-primary)) 14%, var(--color-card))'
                : 'var(--color-card)',
              borderColor: privacyMode
                ? 'color-mix(in srgb, rgb(var(--accent-primary)) 40%, var(--color-border))'
                : 'var(--color-border)',
            }}
            className="inline-flex h-8 w-8 items-center justify-center
                       rounded-lg border
                       hover:opacity-80
                       transition-colors duration-150"
            title={privacyMode ? 'Blur mode ON — click to disable' : 'Enable blur mode (hide $ amounts)'}
            aria-label="Toggle blur mode"
            aria-pressed={privacyMode}
          >
            {privacyMode ? (
              <EyeOff className="w-4 h-4 text-accent" />
            ) : (
              <Eye className="w-4 h-4 text-theme-muted" />
            )}
          </button>

          <button
            onClick={toggleTheme}
            type="button"
            data-tour="theme-toggle"
            style={{
              backgroundColor: 'var(--color-card)',
              borderColor: 'var(--color-border)'
            }}
            className="inline-flex h-8 w-8 items-center justify-center
                       rounded-lg border
                       hover:opacity-80
                       transition-colors duration-150"
            title={`Switch to ${theme === "light" ? "Dark" : "Light"} Mode`}
            aria-label="Toggle theme"
          >
            {theme === "light" ? (
              <Moon className="w-4 h-4 text-theme-muted" />
            ) : (
              <Sun className="w-4 h-4 text-theme-muted" />
            )}
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
